import classNames from 'classnames';
import type { Props as NumberInputProps } from 'components/NumberInput';
import NumberInput from 'components/NumberInput';
import Tooltip from 'components/Tooltip';
import React, { useRef } from 'react';
import FontAwesome from 'react-fontawesome';
import type { Duration } from 'types/units';
import type { Merge } from 'types/utility';
import { Label } from '../Label';
import styles from './index.module.scss';

type DurationInputVariants = 'light' | 'dark';
type DurationInputSizes = 'default' | 'small' | 'large';
interface DurationInputProps {
  value: number;
  variant?: DurationInputVariants;
  size?: DurationInputSizes;
  disabled: boolean;
  className?: string;
}

type Props = Merge<NumberInputProps, DurationInputProps>;

export const DurationInput = ({
  value,
  size = 'default',
  variant = 'light',
  disabled = false,
  className,
  ...rest
}: Props) => {
  // We use a ref to select the input because onFocus={e => event.target.select()}
  // doesn't work in Safari. See https://stackoverflow.com/questions/54229359/why-does-select-not-work-on-safari-with-reactjs
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className={classNames(
        styles.durationInput,
        styles[`variant-${variant}`],
        styles[`size-${size}`],
        {
          [styles.short]: value < 10,
        },
        className
      )}
    >
      <NumberInput
        onClick={() => inputRef.current?.select()}
        ref={inputRef}
        className={styles.input}
        autoComplete="off"
        placeholder="00"
        max="24"
        min="0"
        value={value}
        disabled={disabled}
        tag="input"
        {...rest}
      />
    </div>
  );
};

interface DurationGroupProps {
  duration: Duration;
  onChange: (hours: number, minutes: number) => void;
  namePrefix?: 'estimated';
  label: string;
  infoTooltip?: string | null;
  inline?: boolean;
  variant?: DurationInputVariants;
  size?: DurationInputSizes;
  disabled: boolean;
  className?: string;
  containerClass?: string;
  inputClass?: string;
  timerButton?: React.ReactNode;
}

export const DurationGroup = React.memo(
  ({
    label,
    namePrefix,
    duration,
    onChange,
    infoTooltip,
    inline,
    className = '',
    containerClass,
    inputClass,
    timerButton,
    disabled,
    ...rest
  }: DurationGroupProps) => {
    const handleChangeHours = (value: number) => {
      onChange(value, duration.minutes);
    };
    const handleChangeMinutes = (value: number) => {
      onChange(duration.hours, value);
    };
    return (
      <div
        className={classNames(
          styles.container,
          containerClass,
          styles[`variant-${rest.variant || 'default'}`],
          {
            [styles.inline]: inline,
          }
        )}
      >
        <Label>{label}</Label>
        <div
          className={classNames(
            styles.hhmmInputGroup,
            className,
            styles[`variant-${rest.variant || 'default'}`],
            styles[`size-${rest.size || 'default'}`]
          )}
        >
          <DurationInput
            className={inputClass}
            name={namePrefix ? `${namePrefix}_hours` : 'hours'}
            value={duration.hours}
            onChange={handleChangeHours}
            disabled={disabled}
            {...rest}
          />
          <div className={styles.postfix}>h</div>
          <DurationInput
            className={inputClass}
            name={namePrefix ? `${namePrefix}_minutes` : 'minutes'}
            max="59"
            value={duration.minutes}
            onChange={handleChangeMinutes}
            disabled={disabled}
            {...rest}
          />
          <div className={styles.postfix}>m</div>
          {timerButton}
          {infoTooltip && (
            <Tooltip title={infoTooltip} className={styles.infoTooltip}>
              <FontAwesome name="info-circle" />
            </Tooltip>
          )}
        </div>
      </div>
    );
  }
);
